/**
 * Stale-bundle detection for the auth-failure boundary.
 *
 * A single-page app keeps the JavaScript it booted with until the tab is
 * reloaded, so every fix we ship is invisible to a tab that stayed open — a
 * day after 3.0.21 went live, 104 v1 users were still timing out on the
 * 3.0.20 bundle and 5 v2 users were looping on /login with 3.0.18's broken
 * renewal (V1-CONSOLE-7PM, V2-CONSOLE-209). Sessions failing is exactly the
 * moment a stale bundle hurts most, and also the one moment a reload costs
 * the user nothing (they are being sent to the login page anyway).
 *
 * Vite hashes the entry chunk per build and index.html is served no-store,
 * so "is the entry script this document loaded still the one index.html
 * points at?" answers "has a newer build been deployed?" without any version
 * plumbing. The dev server's unhashed `/src/main.tsx` entry is skipped.
 */
let lastCheckAt = 0

const CHECK_THROTTLE_MS = 60_000

/**
 * Resolves true when a newer build is deployed and the page is reloading —
 * callers must then do nothing else (no redirect, no error screen). Resolves
 * false when this bundle is current, when the check is throttled, or when
 * the answer cannot be determined; never throws.
 */
export async function reloadIfNewBuildAvailable(): Promise<boolean> {
  const entry = document.querySelector<HTMLScriptElement>(
    'script[type="module"][src]',
  )?.src
  if (!entry) return false
  const entryPath = new URL(entry, window.location.href).pathname
  if (!entryPath.includes("/assets/")) return false
  if (Date.now() - lastCheckAt < CHECK_THROTTLE_MS) return false
  lastCheckAt = Date.now()
  try {
    const res = await fetch("/", {
      cache: "no-store",
      headers: { accept: "text/html" },
    })
    if (!res.ok) return false
    const html = await res.text()
    if (html.includes(entryPath)) return false
    window.location.reload()
    return true
  } catch {
    return false
  }
}
